import React, { useContext, useMemo } from 'react';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import './onboarding-dedicated-manager.scss';

function isOnboardingFlagTrue(value) {
  return value === true || value === 'true';
}

function formatManagerField(value) {
  if (!value || value === 'none') return '';
  return value
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function isValidPhotoUrl(url) {
  if (!url || url === 'none') return false;
  return url.startsWith('http://') || url.startsWith('https://');
}

const StarIcon = () => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='white'>
    <path d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' />
  </svg>
);

const CheckMiniIcon = () => (
  <svg width='11' height='11' viewBox='0 0 11 11' fill='none'>
    <path
      d='M1.5 5.5L4.5 8.5L9.5 2.5'
      stroke='white'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const OnboardingDedicatedManager = () => {
  const { onLoadInfoResponse } = useContext(GlobalStateDataContext);

  const onboardingStatus = useMemo(() => {
    return (
      onLoadInfoResponse?.data?.data?.data?.kitchenOperationObject?.onborading_status ?? {}
    );
  }, [onLoadInfoResponse]);

  const deviceAssigned = isOnboardingFlagTrue(onboardingStatus.device_assigned);

  const manager = useMemo(() => {
    return (
      onLoadInfoResponse?.data?.data?.data?.kitchenOperationObject?.dedicated_account_manager ?? {}
    );
  }, [onLoadInfoResponse]);

  const fullName = formatManagerField(manager.full_name) || 'Your Account Manager';
  const managerId = formatManagerField(manager.manager_id);
  const photoUrl = isValidPhotoUrl(manager.photo_url) ? manager.photo_url : null;

  const initials = fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

  return (
    <div className='ob-am-page'>
      <div className='ob-am-card'>

        {/* ── Hero ── */}
        <div className='ob-am-hero'>
          <div className='ob-am-hero__title-row'>
            <div className='ob-am-hero__icon-title'>
              <div className='ob-am-hero__icon'><StarIcon /></div>
              <h1 className='ob-am-title'>Your Manager is Assigned!</h1>
            </div>
            <div className='ob-am-badge'>
              <span className='ob-am-badge__dot' />
              Assigned
            </div>
          </div>
          <p className='ob-am-subtitle'>
            Great news — a dedicated manager has been assigned to your account. They will personally
            deliver your tablet and walk you through every feature.
          </p>
        </div>

        {/* ── Profile ── */}
        <div className='ob-am-profile'>
          <div className='ob-am-avatar'>
            <div className='ob-am-avatar__circle'>
              {photoUrl ? (
                <img src={photoUrl} alt={fullName} className='ob-am-avatar__photo' />
              ) : (
                <span className='ob-am-avatar__initials'>{initials}</span>
              )}
            </div>
            <span className='ob-am-avatar__check' aria-hidden='true'>
              <CheckMiniIcon />
            </span>
          </div>
          <div className='ob-am-profile__info'>
            <p className='ob-am-profile__name'>{fullName}</p>
            <p className='ob-am-profile__role'>Dedicated Account Manager</p>
            {managerId && (
              <div className='ob-am-profile__id-chip'>
                <span className='ob-am-profile__id-label'>Badge</span>
                <span className='ob-am-profile__id-sep' />
                <strong className='ob-am-profile__id-value'>{managerId}</strong>
              </div>
            )}
          </div>
        </div>

        {/* ── What to Expect ── */}
        <div className='ob-am-expect'>
          <div className='ob-am-expect__item'>
            <span className='ob-am-expect__icon'>📦</span>
            <div>
              <div className='ob-am-expect__label-row'>
                <p className='ob-am-expect__label'>Tablet delivery</p>
                {!deviceAssigned && (
                  <span className='ob-am-expect__pending-badge'>Pending</span>
                )}
              </div>
              <p className='ob-am-expect__desc'>Your manager will hand-deliver your device</p>
            </div>
          </div>
          <div className='ob-am-expect__item'>
            <span className='ob-am-expect__icon'>🎓</span>
            <div>
              <p className='ob-am-expect__label'>Guided setup</p>
              <p className='ob-am-expect__desc'>One-on-one walkthrough of every feature</p>
            </div>
          </div>
          <div className='ob-am-expect__item'>
            <span className='ob-am-expect__icon'>🤝</span>
            <div>
              <p className='ob-am-expect__label'>Ongoing support</p>
              <p className='ob-am-expect__desc'>Your first point of contact for questions</p>
            </div>
          </div>
        </div>

        {/* ── Safety ── */}
        <div className='ob-am-safety'>
          <p className='ob-am-safety__heading'>🔒&nbsp; Your Safety Matters</p>
          <p className='ob-am-safety__body'>
            Your account manager will <strong>never</strong> ask for passwords or payment for your
            device. You&apos;ll see their full profile and unique ID before any visit. If anything
            seems off, contact our corporate office directly — do not share personal details with
            anyone claiming to be your manager.
          </p>
        </div>

      </div>
    </div>
  );
};

export default OnboardingDedicatedManager;
